import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../lib/api';
import Modal from '../ui/Modal';
import AttemptDetailView, {
  type AttemptListItem,
  type AttemptDetail,
  type QuestionRef,
} from '../attempts/AttemptDetailView';

export default function MockTestScoresDrawer({
  open,
  onClose,
  testId,
  testTitle,
}: {
  open: boolean;
  onClose: () => void;
  testId: string;
  testTitle: string;
}) {
  const [page, setPage] = useState(1);
  const [passed, setPassed] = useState<'all' | 'true' | 'false'>('all');
  const [selectedAttempt, setSelectedAttempt] = useState<{ id: string; userId: string } | null>(
    null,
  );

  const { data, isLoading } = useQuery({
    queryKey: ['mock-test-attempts', testId, page, passed],
    enabled: open && !!testId,
    queryFn: async () => {
      const res = await api.get(`/mock-tests/${testId}/attempts`, {
        params: {
          page,
          limit: 10,
          ...(passed !== 'all' ? { passed } : {}),
        },
      });
      return res.data;
    },
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['mock-test-attempt-detail', selectedAttempt?.userId, selectedAttempt?.id],
    enabled: open && !!selectedAttempt,
    queryFn: async () => {
      const res = await api.get(
        `/users/${selectedAttempt!.userId}/attempts/${selectedAttempt!.id}`,
      );
      return res.data.data as { attempt: AttemptDetail; questions: QuestionRef[] };
    },
  });

  const attempts: AttemptListItem[] = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  const handleClose = () => {
    setSelectedAttempt(null);
    setPage(1);
    setPassed('all');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={selectedAttempt ? 'Attempt Detail' : `Scores — ${testTitle}`}
      variant="drawer"
    >
      {selectedAttempt ? (
        <AttemptDetailView
          loading={detailLoading}
          detail={detailData}
          onBack={() => setSelectedAttempt(null)}
        />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <select
              value={passed}
              onChange={(e) => {
                setPassed(e.target.value as 'all' | 'true' | 'false');
                setPage(1);
              }}
              className="input-field w-40"
            >
              <option value="all">All results</option>
              <option value="true">Passed</option>
              <option value="false">Failed</option>
            </select>
            <p className="text-sm text-gray-500 ml-auto">{pagination.total} attempts</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-500">User</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Score</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Result</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Date & Time</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : attempts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-400">
                      No attempts yet
                    </td>
                  </tr>
                ) : (
                  attempts.map((a) => {
                    const total = a.totalCorrect + a.totalWrong + a.totalSkipped;
                    const userLabel = a.userId?.profile?.name || a.userId?.email || 'Unknown user';
                    return (
                      <tr key={a._id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                              <span className="text-primary-700 text-xs font-medium">
                                {userLabel.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {a.userId?.profile?.name || 'Unknown'}
                              </p>
                              {a.userId?.email && (
                                <p className="text-xs text-gray-400">{a.userId.email}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {a.score}/{total}
                        </td>
                        <td className="py-3 px-4">
                          {a.passed === false ? (
                            <span className="badge bg-danger-50 text-danger-700">Failed</span>
                          ) : (
                            <span className="badge bg-success-50 text-success-700">Passed</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {new Date(a.createdAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="py-3 px-4">
                          {a.userId?._id && (
                            <button
                              onClick={() =>
                                setSelectedAttempt({ id: a._id, userId: a.userId!._id })
                              }
                              className="text-xs text-primary-700 hover:underline"
                            >
                              View answers
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary text-xs flex items-center gap-1"
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pagination.totalPages}
                className="btn-secondary text-xs flex items-center gap-1"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
